import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from '../../util/reactIntl';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, NewsletterOptInBanner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import MobileBottomNav from '../TopbarContainer/MobileBottomNav/MobileBottomNav';

import SectionAboutBanner from './sections/SectionAboutBanner/SectionAboutBanner';
import SectionBanners from './sections/SectionBanners/SectionBanners';
import SectionCallToAction from './sections/SectionCallToAction/SectionCallToAction';
import SectionCategoryTiles from './sections/SectionCategoryTiles/SectionCategoryTiles';
import SectionEditorial from './sections/SectionEditorial/SectionEditorial';
import SectionHero from './sections/SectionHero/SectionHero';
import SectionListItemCta from './sections/SectionListItemCta/SectionListItemCta';
import SectionListingStrip from './sections/SectionListingStrip/SectionListingStrip';

import { DRESSES_STRIP, RECENTLY_LISTED_STRIP } from './landingStrips';

import css from './CustomLandingPage.module.css';

/**
 * Custom React landing page for ZUPRIZE.
 *
 * Composes section components from `./sections/`. The hosted PageBuilder
 * variant remains available behind the `USE_HOSTED_LANDING_PAGE` flag in
 * `LandingPage.config.js`.
 *
 * Order (client, Sept 2026):
 * - SectionHero (short band with the search form)
 * - SectionCategoryTiles (a circle per top-level category)
 * - SectionListingStrip: Kjoler
 * - SectionListItemCta (Har du klær du aldri bruker?)
 * - SectionListingStrip: Nylig lagt ut (no chips)
 * - SectionBanners (admin-managed campaigns; nothing when none are active)
 * - SectionAboutBanner (who we are + Om oss CTA)
 * - SectionEditorial
 * - SectionCallToAction (hidden for now)
 *
 * Products now sit high on the page, which was the point. The chip row under
 * Nylig lagt ut is gone: it repeated the category circles a few centimetres
 * below them, which is what made the earlier attempt at this order look
 * confused.
 *
 * @returns {JSX.Element}
 */
const CustomLandingPage = () => {
  const intl = useIntl();
  const scrollingDisabled = useSelector(isScrollingDisabled);

  const title = intl.formatMessage({ id: 'CustomLandingPage.schemaTitle' });
  const description = intl.formatMessage({ id: 'CustomLandingPage.schemaDescription' });

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      title={title}
      description={description}
    >
      <TopbarContainer />
      <NewsletterOptInBanner />
      <main className={css.main}>
        <SectionHero />
        <SectionCategoryTiles />
        <SectionListingStrip {...DRESSES_STRIP} />
        <SectionListItemCta />
        <SectionListingStrip {...RECENTLY_LISTED_STRIP} />
        <SectionBanners />
        <SectionAboutBanner />
        <SectionEditorial />
        {/* Closing "Skandinavisk stil, ansvarlig valg." CTA hidden for
            now per client. Re-enable by uncommenting this line. */}
        {/* <SectionCallToAction /> */}
      </main>
      <FooterContainer />
      <MobileBottomNav />
    </Page>
  );
};

export default CustomLandingPage;
